// @ts-strict-ignore
import { v4 as uuidv4 } from 'uuid';
import type { FlowState } from '~/flow/types';
import buildDependencies from '../../engine/dependencies';

import type {
  WorkflowOperationMessage,
  WorkflowPropsResponse,
  WorkflowFailureHandlingMode,
  Action,
  Threshold,
  DataRoute,
} from '../../engine/types';
import {
  OperationMessageType,
} from '../../engine/types';
import type { WorkflowState } from '../state';
import { initialState } from '../state';
import { compileWorkflowDataDependencies, snapshotAndCloneWorkflowData } from '../helpers';
import type { FeatureFlags } from '~/helpers/typescript';
import { WorkflowOperationType } from '../../engine/constants';
import { transformWorkflowInitializationData } from '../utils/workflowTransformation';
import { createSnapshot } from 'flow/service/history';
import { initSnippetCollapsedState } from './ui';

// ================ General Reducers ================

export function initWorkflowPropsAndData(
  state: WorkflowState,
  action: {
    payload: {
      workflowProps: WorkflowPropsResponse;
      workflowData: FlowState;
      featureFlags: Partial<FeatureFlags>;
    };
  }) {
  const {
    workflowData, workflowProps, featureFlags,
  } = action.payload;

  // Use the extracted transformation function
  const {
    initWorkflowProps,
    initWorkflowData,
    legacyMappings,
    initWorkflowFeatures,
    featureFlagsForStore,
  } = transformWorkflowInitializationData({
    workflowData,
    workflowProps,
    featureFlags,
  });

  // Populate nodeContainers.snippets
  initSnippetCollapsedState(state, {
    payload: { actions: Object.values(initWorkflowData.actions) },
  });

  state.legacyMappings = legacyMappings;
  state.workflowData = initWorkflowData;
  state.workflowProps = initWorkflowProps;
  state.workflowSnapshots = {
    workflowData: {
      history: {
        initial: createSnapshot({ data: initWorkflowData }),
        past: [],
      },
    },
    workflowProps: {
      history: {
        initial: createSnapshot({ data: initWorkflowProps }),
      },
    },
  };

  state.workflowFeatures = initWorkflowFeatures;

  // This is for adding relevant feature flags to the redux store,
  // specifically utilized for selectors/reducers/thunks (not components)
  state.featureFlagsForStore = featureFlagsForStore;
}

export function replaceWorkflowDataGraph(
  state: WorkflowState,
  action: {
    payload: {
      actions: Record<string, Action>;
      thresholds: Record<string, Threshold>;
      dataRoutes: Record<string, DataRoute>;
    };
  },
) {
  const { actions, thresholds, dataRoutes } = action.payload;

  const workflowDataToUpdate: WorkflowState['workflowData'] = {
    ...state.workflowData,
    actions,
    thresholds,
    dataRoutes,
  };

  state.workflowData = compileWorkflowDataDependencies(workflowDataToUpdate);
}

export function replaceWorkflowProps(
  state: WorkflowState,
  action: { payload: Partial<WorkflowState['workflowProps']> },
) {
  state.workflowProps = {
    ...state.workflowProps,
    ...action.payload,
  };
}

export function resetWorkflowEditorState(): WorkflowState {
  return initialState;
}

export function updateDependencies(
  state: WorkflowState,
) {
  const regeneratedDependencies = buildDependencies({
    actions: state.workflowData.actions,
    thresholds: state.workflowData.thresholds,
    dataRoutes: state.workflowData.dataRoutes,
  });

  state.workflowData = {
    ...state.workflowData,
    dependencies: regeneratedDependencies,
  };
}

export function updateSdkConfig(
  state: WorkflowState,
  action: { payload: WorkflowState['workflowData']['sdkConfig'] },
) {
  state.workflowData.sdkConfig = action.payload;
}


export function updateServiceActionValidation(
  state: WorkflowState,
  action: {
    payload: {
      publicId: string;
      data: string[];
    };
  },
) {
  const { publicId, data } = action.payload;
  if (publicId && state.workflowData.actions[publicId]) {
    state.workflowData.actions[publicId].validation = data;
  }
}

export function applyPrevWorkflowDataSnapshot(
  state: WorkflowState,
  action: {
    payload: {
      publicId?: string;
      data: any; // we literally would accept anything as of now
    };
  },
) {
  const { publicId, data } = action.payload;
  const pastHistoryLength = state.workflowSnapshots.workflowData.history.past.length;

  state.workflowData = pastHistoryLength <= 1
    ? state.workflowSnapshots.workflowData.history.initial.data
    : state.workflowSnapshots.workflowData.history.past.at(-2).data;

  const { workflowSnapshots } = snapshotAndCloneWorkflowData(state);
  state.workflowSnapshots = workflowSnapshots;

  const newOperationMsg = {
    id: uuidv4(),
    publicId: publicId || '',
    type: OperationMessageType.undo,
    timestamp: Date.now(),
    message: WorkflowOperationType.UNDO_PERFORMED,
    data: data || undefined,
  };

  state.workflowOperationDescriptions.push(newOperationMsg);
}

export function dismissWorkflowOperationMessage(
  state: WorkflowState,
  action: {
    payload: {
      id: string;
    };
  },
) {
  const { workflowOperationDescriptions } = state;
  const { id } = action.payload;

  const filteredOperationDesc = workflowOperationDescriptions.filter(descMsgObject => (
    descMsgObject.id !== id
    && (descMsgObject.timestamp - Date.now()) >= 300000), // anything beyond 5 mins ago will be cleared too
  );
  state.workflowOperationDescriptions = filteredOperationDesc;
}

export function createWorkflowOperationMessage(
  state: WorkflowState,
  action: {
    payload: {
      object: WorkflowOperationMessage;
    };
  },
) {
  const { object } = action.payload;

  state.workflowOperationDescriptions.push(object);
}

export function updateFailureHandlingMode(
  state: WorkflowState,
  action: {
    payload: {
      mode: WorkflowFailureHandlingMode;
    };
  },
) {
  const { mode } = action.payload;

  state.workflowData.failureHandlingMode = mode;
}
